import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lightning, Queue as QueueIcon, Star } from "@phosphor-icons/react";

export const Queue = ({ items, onRefresh }) => {
  const { user } = useAuth();
  const [payingId, setPayingId] = useState(null);

  const skipLine = async (trackId) => {
    setPayingId(trackId);
    try {
      const res = await api.post("/payments/checkout", { track_id: trackId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPayingId(null);
    }
  };

  const renderTrailing = (t, isOwner) => {
    if (isOwner && !t.priority) {
      return (
        <Button
          data-testid="priority-skip-submit-button"
          onClick={() => skipLine(t.id)}
          disabled={payingId === t.id}
          className="bg-transparent border border-[#E1FF00]/50 text-[#E1FF00] hover:bg-[#E1FF00] hover:text-black font-bold rounded-none text-xs px-3 py-2 h-auto gap-1 shrink-0 shadow-[0_0_15px_rgba(225,255,0,0.15)] hover:-translate-y-0.5 transition-all"
        >
          <Lightning size={14} weight="fill" /> {payingId === t.id ? "…" : "Skip $5"}
        </Button>
      );
    }
    if (t.rating_count > 0) {
      return (
        <div className="font-mono text-xs text-[#E1FF00] flex items-center gap-1 shrink-0">
          <Star size={12} weight="fill" /> {t.avg_rating}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#0F0F13] border border-white/5 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <h3 className="font-head font-bold text-lg tracking-tight flex items-center gap-2">
          <QueueIcon size={20} className="text-[#0038FF]" /> Up Next
        </h3>
        <span className="font-mono text-xs text-[#52525B]">{items.length} in queue</span>
      </div>

      <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto" data-testid="queue-list">
        <AnimatePresence initial={false}>
          {items.map((t, i) => {
            const isOwner = user && t.user_id === user.user_id;
            return (
              <motion.div
                key={t.id}
                data-testid="queue-track-item"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex items-center gap-4 px-6 py-4 ${t.priority ? "bg-[#E1FF00]/[0.04]" : ""}`}
              >
                <div className="font-mono text-lg font-bold w-7 shrink-0 text-center" style={{ color: t.priority ? "#E1FF00" : "#52525B" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{t.title}</p>
                    {t.priority && <Lightning size={14} weight="fill" className="text-[#E1FF00] shrink-0" />}
                  </div>
                  <p className="text-sm text-[#A1A1AA] truncate">{t.artist_name} · <span className="text-[#B14AED]">{t.genre}</span></p>
                </div>
                {renderTrailing(t, isOwner)}
              </motion.div>
            );
          })}
        </AnimatePresence>
        {items.length === 0 && (
          <div className="px-6 py-12 text-center text-[#52525B] text-sm">Queue is empty. Submit a track to fill it up.</div>
        )}
      </div>
    </div>
  );
};
